import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { fromTable } from "@/lib/supabase-helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckinButton } from "@/components/lessons/CheckinButton";
import { Clock, MapPin, AlertCircle } from "lucide-react";

/**
 * Lista aulas matriculadas do aluno logado para o dia de hoje (TZ Brasil)
 * com botao de check-in por aula. Reutilizavel em:
 * - Pagina /perfil tab "lessons" (web desktop e mobile)
 * - Pagina standalone /minhas-aulas (back-compat)
 * - PWA/mobile via QuickCheckinFAB (mostra so aulas ativas na janela)
 *
 * Layout e totalmente responsivo (mobile-first).
 */

const CLUB_TZ = "America/Sao_Paulo";

interface Enrollment {
  lesson_id: string;
  club_lessons: {
    id: string;
    title: string;
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_active: boolean;
    specific_date: string | null;
    is_recurring: boolean;
    clubs_public: { id: string; name: string } | null;
  } | null;
}

function todayInClubTz(): { isoDate: string; dayOfWeek: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CLUB_TZ,
    year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
  }).formatToParts(new Date());
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const dayOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(m.weekday);
  return { isoDate: `${m.year}-${m.month}-${m.day}`, dayOfWeek };
}

export function MyLessonsSection() {
  const { t } = useTranslation("myLessons");
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [attendanceToday, setAttendanceToday] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      const { data: students } = await supabase
        .from("club_students")
        .select("id")
        .eq("user_id", userId)
        .eq("status", "active");
      const ids = (students ?? []).map((s) => s.id);
      setStudentIds(ids);
      if (ids.length === 0) {
        setEnrollments([]);
        setLoading(false);
        return;
      }
      const { data: enr } = await fromTable("club_lesson_students")
        // `clubs_public`: a tabela `clubs` é legível só por staff/admin, então
        // para o aluno este embed voltava null e o nome do clube sumia do card.
        .select("lesson_id, club_lessons(id, title, day_of_week, start_time, end_time, is_active, specific_date, is_recurring, clubs_public(id, name))")
        .in("student_id", ids);
      setEnrollments((enr as Enrollment[] | null) ?? []);

      const today = todayInClubTz().isoDate;
      const { data: att } = await supabase
        .from("lesson_attendance")
        .select("lesson_id")
        .in("student_id", ids)
        .eq("lesson_date", today);
      setAttendanceToday(
        new Set(
          (att ?? []).map((a: { lesson_id: string }) => a.lesson_id),
        ),
      );
      setLoading(false);
    })();
  }, [userId]);

  if (userId === undefined) {
    return <p className="text-sm text-muted-foreground">{t("myLessons.loadingUser")}</p>;
  }
  if (userId === null) {
    return <p className="text-sm text-muted-foreground">{t("myLessons.notLinked")}</p>;
  }

  const { isoDate, dayOfWeek } = todayInClubTz();

  const todaysLessons = enrollments
    .map((e) => e.club_lessons)
    .filter((l): l is NonNullable<Enrollment["club_lessons"]> => !!l && l.is_active)
    .filter((l) => (l.is_recurring ? l.day_of_week === dayOfWeek : l.specific_date === isoDate))
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  if (loading) {
    return <p className="text-sm text-muted-foreground">{t("myLessons.loadingLessons")}</p>;
  }

  if (studentIds.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6 text-center space-y-2">
          <AlertCircle className="h-6 w-6 mx-auto text-amber-500" />
          <p className="text-sm">{t("myLessons.notLinked")}</p>
        </CardContent>
      </Card>
    );
  }

  if (todaysLessons.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6 text-center text-muted-foreground text-sm">
          {t("myLessons.empty")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {todaysLessons.map((lesson) => (
        <Card key={lesson.id}>
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="flex items-center justify-between gap-2 text-sm sm:text-base">
              <span className="truncate">{lesson.title}</span>
              {attendanceToday.has(lesson.id) && (
                <Badge variant="secondary" className="shrink-0 text-[10px] sm:text-xs">
                  {t("myLessons.attendanceRegistered")}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 px-4 sm:px-6">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {lesson.start_time.slice(0, 5)} — {lesson.end_time.slice(0, 5)}
              </span>
              {lesson.clubs_public?.name && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{lesson.clubs_public.name}</span>
                </span>
              )}
            </div>
            <CheckinButton
              lessonId={lesson.id}
              lessonDate={isoDate}
              startTime={lesson.start_time}
              endTime={lesson.end_time}
              alreadyCheckedIn={attendanceToday.has(lesson.id)}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
